import React from 'react';
import { useResetPassword, getStrengthColor } from './hooks/useResetPassword';

const EyeIcon = ({ className }) => (
  <svg xmlns="http://www.w3.org/2000/svg" className={className} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z"/><circle cx="12" cy="12" r="3"/></svg>
);
const EyeOffIcon = ({ className }) => (
  <svg xmlns="http://www.w3.org/2000/svg" className={className} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M17.94 17.94A10.07 10.07 0 0 1 12 20c-7 0-11-8-11-8a18.45 18.45 0 0 1 5.06-5.94M9.9 4.24A9.12 9.12 0 0 1 12 4c7 0 11 8 11 8a18.5 18.5 0 0 1-2.16 3.19m-6.72-1.07a3 3 0 1 1-4.24-4.24"/><line x1="1" y1="1" x2="23" y2="23"/></svg>
);

const PasswordField = ({ id, label, value, onChange, show, onToggle, testId }) => (
  <div className="mb-4">
    <label htmlFor={id} className="block text-sm font-medium text-gray-300 mb-2">{label}</label>
    <div className="relative">
      <input id={id} data-testid={testId} type={show ? 'text' : 'password'} value={value} onChange={onChange} required
        className="w-full px-4 py-3 pr-12 bg-gray-700 border border-gray-600 rounded-lg text-white placeholder-gray-400 focus:outline-hidden focus:ring-2 focus:ring-green-500 focus:border-transparent transition" />
      <button type="button" tabIndex={-1} onClick={onToggle} className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-200">
        {show ? <EyeOffIcon className="h-5 w-5" /> : <EyeIcon className="h-5 w-5" />}
      </button>
    </div>
  </div>
);

const ResetSuccessView = ({ navigate }) => (
  <div className="min-h-screen bg-gray-900 flex items-center justify-center p-4">
    <div className="max-w-md w-full bg-gray-800 rounded-2xl shadow-xl p-8 border border-gray-700">
      <div className="w-16 h-16 bg-green-900/50 rounded-full flex items-center justify-center mx-auto mb-6">
        <svg className="w-8 h-8 text-green-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
        </svg>
      </div>
      <h2 className="text-2xl font-bold text-center text-white mb-4">Password Reset Successful</h2>
      <p className="text-gray-300 text-center mb-4">You can now log in with your new password. All previous sessions have been invalidated.</p>
      <button onClick={() => navigate('/admin/login')} className="w-full bg-linear-to-r from-green-500 to-emerald-600 text-white font-semibold py-3 rounded-lg transition">
        Continue to Admin Login
      </button>
      <p className="text-xs text-gray-500 text-center mt-4">Redirecting automatically in 3 seconds...</p>
    </div>
  </div>
);

const StrengthIndicator = ({ newPassword, strength }) => (
  <div className="mb-4 -mt-2">
    <div className="flex items-center justify-between mb-1">
      <span className="text-xs text-gray-400">Strength:</span>
      <span className={`text-xs font-medium ${getStrengthColor(strength.score, 'text')}`}>{strength.message}</span>
    </div>
    <div className="w-full h-1.5 bg-gray-700 rounded-full overflow-hidden">
      <div className={`h-full transition-all ${getStrengthColor(strength.score, 'bg')}`} style={{ width: `${(strength.score / 5) * 100}%` }} />
    </div>
    <div className="mt-2 space-y-1">
      <p className={`text-xs ${newPassword.length >= 8 ? 'text-green-400' : 'text-gray-500'}`}>{newPassword.length >= 8 ? '\u2713' : '\u25CB'} At least 8 characters</p>
      <p className={`text-xs ${/[A-Z]/.test(newPassword) && /[a-z]/.test(newPassword) ? 'text-green-400' : 'text-gray-500'}`}>{/[A-Z]/.test(newPassword) && /[a-z]/.test(newPassword) ? '\u2713' : '\u25CB'} Upper & lowercase</p>
      <p className={`text-xs ${/\d/.test(newPassword) ? 'text-green-400' : 'text-gray-500'}`}>{/\d/.test(newPassword) ? '\u2713' : '\u25CB'} At least one number</p>
    </div>
  </div>
);

const AdminResetPasswordPage = () => {
  const {
    newPassword, setNewPassword, confirmPassword, setConfirmPassword,
    showNew, setShowNew, showConfirm, setShowConfirm,
    loading, success, error, token, strength, navigate, handleSubmit,
  } = useResetPassword();

  if (success) return <ResetSuccessView navigate={navigate} />;

  return (
    <div className="min-h-screen bg-gray-900 flex items-center justify-center p-4">
      <div className="max-w-md w-full bg-gray-800 rounded-2xl shadow-xl p-8 border border-gray-700">
        <button onClick={() => navigate('/admin/login')} className="flex items-center text-gray-400 hover:text-white mb-6 transition">
          <svg className="w-5 h-5 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 19l-7-7m0 0l7-7m-7 7h18" />
          </svg>
          Back to Admin Login
        </button>
        <div className="w-16 h-16 bg-linear-to-br from-green-500 to-emerald-600 rounded-full flex items-center justify-center mx-auto mb-6">
          <svg className="w-8 h-8 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 7a2 2 0 012 2m4 0a6 6 0 01-7.743 5.743L11 17H9v2H7v2H4a1 1 0 01-1-1v-2.586a1 1 0 01.293-.707l5.964-5.964A6 6 0 1121 9z" />
          </svg>
        </div>
        <h1 className="text-2xl font-bold text-center text-white mb-2">Set New Password</h1>
        <p className="text-gray-400 text-center mb-8">Choose a strong password for your admin account</p>
        {error && <div className="mb-6 p-4 bg-red-900/30 border border-red-700 rounded-lg"><p className="text-red-400 text-sm">{error}</p></div>}
        <form onSubmit={handleSubmit}>
          <PasswordField id="admin-new-pw" label="New Password" value={newPassword} onChange={e => setNewPassword(e.target.value)} show={showNew} onToggle={() => setShowNew(!showNew)} testId="admin-reset-new-password" />
          {newPassword && <StrengthIndicator newPassword={newPassword} strength={strength} />}
          <PasswordField id="admin-confirm-pw" label="Confirm Password" value={confirmPassword} onChange={e => setConfirmPassword(e.target.value)} show={showConfirm} onToggle={() => setShowConfirm(!showConfirm)} testId="admin-reset-confirm-password" />
          {confirmPassword && (
            <p className={`text-xs -mt-2 mb-4 ${confirmPassword === newPassword ? 'text-green-400' : 'text-red-400'}`}>
              {confirmPassword === newPassword ? '\u2713 Passwords match' : 'Passwords do not match'}
            </p>
          )}
          <button type="submit" disabled={loading || !token} data-testid="admin-reset-submit"
            className="w-full bg-linear-to-r from-green-500 to-emerald-600 hover:from-green-600 hover:to-emerald-700 text-white font-semibold py-3 rounded-lg transition disabled:opacity-50">
            {loading ? 'Resetting...' : 'Reset Password'}
          </button>
        </form>
        <p className="text-xs text-gray-500 text-center mt-6">This link expires in 15 minutes for security.</p>
      </div>
    </div>
  );
};

export default AdminResetPasswordPage;
